// pipe (grid UX): the SHARED interactive ERP table — the @affine/component `Table` plus the
// cell affordances every ERP grid inherits by swapping `Table` for `ErpTable` (one shared
// presenter, the SortableHeaderCell / TableFilterInput pattern):
//
//   • double-click a body cell → copy its rendered text (with a "Copied to clipboard" toast);
//   • right-click a body cell → the design-system context menu with a Copy item (radix
//     ContextMenu: cursor-anchored, Escape/outside-click dismiss, mobile long-press for free);
//   • hover a CLIPPED cell → a native `title` tooltip reveals the full value plus a dotted
//     underline affordance (only while the cell is actually truncated — widening the column via
//     the SortableHeaderCell resize handle past the content removes it).
//
// All three ride ONE set of delegated handlers on the <table> element — no per-cell handlers, so
// the memoized-row perf work is untouched. Redaction safety is by construction: every copy reads
// the DOM's RENDERED text, so a masked cell copies its localized marker, never a raw value.
//
// It is ALSO where every ERP grid becomes responsive, for all 27 call sites at once and with zero
// call-site changes: this component applies the shared `erpGrid` class and stamps the grid's live
// COLUMN COUNT and per-column HEADER LABELS onto the <table> as custom properties. The count gives
// the table a real `min-width` (so a squeezed fixed-layout grid scrolls instead of collapsing its
// columns toward zero), and the labels are what card mode renders as each value's `::before`, so a
// phone reader sees "Result: Pass" rather than a bare "Pass". Both live on the TABLE — one write
// per column, never per cell — which is what keeps a 2,853-row × 12-column report cheap.
//
// KNOWN CARD-MODE LIMITATION (deliberate, not an oversight): card mode hides the header row, so
// the SortableHeaderCell's sort toggle and resize handle have no affordance below the breakpoint.
// The per-page filter box, the copy gestures and every row action stay available; a narrow-width
// sort control would be new UI with new strings, so it is a follow-up, not a silent regression.
//
// Import discipline (load-bearing, mirrors erp-table-controls.tsx): @affine/component ui/*
// subpaths ONLY (the portal consumes this package and must not pull the barrel), @affine/i18n
// (mocked under happy-dom), @blocksuite/icons/rc, React, clsx, and the pure clipboard util + pure
// style modules. NO runtime ErpService / workbench / DI / Electron — and NO @affine/core.
import { ContextMenu, MenuItem } from '@affine/component/ui/menu';
import { Table } from '@affine/component/ui/table';
import { toast } from '@affine/component/ui/toast';
import { useI18n } from '@affine/i18n';
import { CopyIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import {
  type ComponentProps,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';

import { copyTextToClipboard } from './clipboard';
import {
  ERP_COL_COUNT_VAR,
  ERP_INTERACTIVE_SELECTOR,
  ERP_MAX_LABELLED_COLS,
  erpColLabelVar,
} from './erp-breakpoints';
import * as styles from './erp-table.css';

export type ErpTableProps = ComponentProps<typeof Table>;

function cellFromEvent(e: ReactMouseEvent): HTMLTableCellElement | null {
  const target = e.target instanceof Element ? e.target : null;
  const cell = target?.closest('td');
  return cell && !cell.closest('thead') ? cell : null;
}

// `ERP_INTERACTIVE_SELECTOR` (erp-breakpoints) is the shared "this is a control" predicate. Here it
// marks the targets that keep their own gesture semantics — a copy gesture landing ON one never
// copies, and the touch gate below never swallows their pointerdown (the transition dropdown and
// the column-resize separator must keep working on touch). The SAME predicate exempts a cell from
// the width-discipline clip in erp-screen.css; the two must agree, or a control the gestures step
// around is a control the clip can still hide.
function isInteractiveTarget(e: ReactMouseEvent): boolean {
  const target = e.target instanceof Element ? e.target : null;
  return !!target?.closest(ERP_INTERACTIVE_SELECTOR);
}

/** A cell's DATA text: the rendered (post-redaction) text minus any embedded controls' captions
 *  (an actions cell must never copy "Edit Advance…" as if it were a value). */
function cellCopyText(cell: HTMLTableCellElement): string {
  if (!cell.querySelector(ERP_INTERACTIVE_SELECTOR)) {
    return (cell.textContent ?? '').trim();
  }
  const clone = cell.cloneNode(true) as HTMLTableCellElement;
  for (const el of Array.from(
    clone.querySelectorAll(ERP_INTERACTIVE_SELECTOR)
  )) {
    el.remove();
  }
  return (clone.textContent ?? '').trim();
}

/**
 * The rendered (post-redaction) text of the copyable BODY cell under an event, or null when the
 * event landed outside a body cell, on an interactive control (a dblclick on "Edit" must never
 * copy), or on a cell with no data text (nothing to copy — the native menu stays available).
 */
function copyableCellText(e: ReactMouseEvent): string | null {
  const cell = cellFromEvent(e);
  if (!cell || isInteractiveTarget(e)) return null;
  const text = cellCopyText(cell);
  return text.length > 0 ? text : null;
}

/** A DOM label as a CSS <string> token. `JSON.stringify` already escapes the two characters a CSS
 *  string cannot carry raw (`"` and `\`); collapsing whitespace first keeps a multi-line header
 *  (an icon-only column that still holds a caption) from smuggling a newline into the literal. */
function cssStringLiteral(label: string): string {
  return JSON.stringify(label.replace(/\s+/g, ' '));
}

/** The header row's cells, in column order — the label SOURCE for card mode. */
function headerCellsOf(table: HTMLTableElement): Element[] {
  const head = table.querySelector('thead');
  const row = head?.querySelector('tr');
  if (!row) return [];
  return Array.from(row.children).filter(
    el => el.tagName === 'TD' || el.tagName === 'TH'
  );
}

export function ErpTable({
  className,
  onContextMenuCapture,
  onDoubleClick,
  onMouseOut,
  onMouseOver,
  onPointerDownCapture,
  ...tableProps
}: ErpTableProps) {
  const t = useI18n();
  const tableRef = useRef<HTMLTableElement | null>(null);
  // The last stamped column signature. Re-stamping is O(columns), but only when the columns
  // actually changed — a filter keystroke re-renders the screen, not the header row.
  const stampedRef = useRef<string | null>(null);

  // Written imperatively (not through a `style` prop) on purpose: these are presentation-only
  // custom properties DERIVED from the caller's own header row, so routing them through state
  // would re-render every grid on mount to tell it what it already rendered. No ErpTable call site
  // passes `style`, so nothing else owns the table's inline style to clobber it. `useLayoutEffect`
  // lands the stamp before paint — the grid never flashes at the wrong width.
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const labels = headerCellsOf(table).map(cell =>
      (cell.textContent ?? '').trim()
    );
    const signature = labels.join('\u0000');
    if (signature === stampedRef.current) return;
    stampedRef.current = signature;
    table.style.setProperty(ERP_COL_COUNT_VAR, String(labels.length));
    for (let index = 1; index <= ERP_MAX_LABELLED_COLS; index++) {
      const name = erpColLabelVar(index);
      const label = labels[index - 1];
      // An unlabelled column (the actions column) must CLEAR its property rather than set an
      // empty string: an unset property makes `content: var(…)` invalid at computed-value time,
      // so no `::before` box is generated at all — an empty string would render a stray gap.
      if (label) table.style.setProperty(name, cssStringLiteral(label));
      else table.style.removeProperty(name);
    }
  });
  // The cell text captured when a context-menu gesture starts (right-click via the capture gate
  // below; touch/pen via the pointerdown gate). A ref, not state — the menu's content ("Copy")
  // is static, so opening never needs a re-render.
  const menuTextRef = useRef('');

  const copyText = useCallback(
    (text: string) => {
      copyTextToClipboard(text)
        .then(ok => {
          if (ok) toast(t['Copied to clipboard']());
        })
        .catch(() => {
          // Clipboard unavailable (non-secure context): the affordance stays silent — a copy
          // gesture must never crash the grid.
        });
    },
    [t]
  );

  const handleDoubleClick = useCallback(
    (e: ReactMouseEvent<HTMLTableElement>) => {
      onDoubleClick?.(e);
      const text = copyableCellText(e);
      if (text) copyText(text);
    },
    [copyText, onDoubleClick]
  );

  // Capture-phase gate for the radix ContextMenu trigger below: stopping propagation here means
  // the radix bubble-phase listener never sees the event on a NON-copyable target (header,
  // button, empty cell), so it neither opens nor calls preventDefault — the browser's native
  // menu stays available exactly there. The ref is cleared too, so a later timer-opened menu
  // can never carry a previous cell's text.
  const handleContextMenuCapture = useCallback(
    (e: ReactMouseEvent<HTMLTableElement>) => {
      onContextMenuCapture?.(e);
      const text = copyableCellText(e);
      if (!text) {
        menuTextRef.current = '';
        e.stopPropagation();
        return;
      }
      menuTextRef.current = text;
    },
    [onContextMenuCapture]
  );

  // Touch/pen gate. The radix trigger arms its OWN 700ms long-press timer on pointerdown —
  // independent of contextmenu, which iOS never even fires — so the contextmenu gate alone
  // would leave iOS with a dead Copy and Android able to open the menu holding a PREVIOUS
  // cell's text. Gating here: a copyable cell feeds the ref (iOS long-press copy works); a
  // non-copyable, non-interactive target is swallowed before radix can arm the timer (native
  // behavior preserved); an interactive target keeps its event (dropdown triggers, the resize
  // separator) with the ref cleared, so a long-press there opens at most a no-op Copy.
  const handlePointerDownCapture = useCallback(
    (e: ReactPointerEvent<HTMLTableElement>) => {
      onPointerDownCapture?.(e);
      if (e.pointerType === 'mouse') return;
      const text = copyableCellText(e);
      if (text) {
        menuTextRef.current = text;
        return;
      }
      menuTextRef.current = '';
      if (!isInteractiveTarget(e)) e.stopPropagation();
    },
    [onPointerDownCapture]
  );

  // Truncation is re-evaluated on every hover (a column resize or filter changes it). The title
  // tooltip is added only while the text is actually clipped ("hover reveals what the width
  // hides") and removed only when WE set it (the data-erp-title marker) — never a caller's own.
  const handleMouseOver = useCallback(
    (e: ReactMouseEvent<HTMLTableElement>) => {
      onMouseOver?.(e);
      const target = e.target instanceof Element ? e.target : null;
      const cell = target?.closest('td');
      if (!cell) return;
      const text = cellCopyText(cell);
      if (text && cell.scrollWidth > cell.clientWidth) {
        cell.setAttribute('data-truncated', 'true');
        if (!cell.getAttribute('title')) {
          cell.setAttribute('title', text);
          cell.setAttribute('data-erp-title', 'true');
        }
      } else {
        if (cell.getAttribute('data-erp-title')) {
          cell.removeAttribute('title');
          cell.removeAttribute('data-erp-title');
        }
        cell.removeAttribute('data-truncated');
      }
    },
    [onMouseOver]
  );

  // Leaving a cell retracts the reveal — a splice-in-place row mutation must never leave a
  // stale pre-mutation tooltip pinned on the reused DOM node.
  const handleMouseOut = useCallback(
    (e: ReactMouseEvent<HTMLTableElement>) => {
      onMouseOut?.(e);
      const target = e.target instanceof Element ? e.target : null;
      const cell = target?.closest('td');
      if (!cell) return;
      if (cell.getAttribute('data-erp-title')) {
        cell.removeAttribute('title');
        cell.removeAttribute('data-erp-title');
      }
      cell.removeAttribute('data-truncated');
    },
    [onMouseOut]
  );

  const handleCopySelect = useCallback(() => {
    if (menuTextRef.current) copyText(menuTextRef.current);
  }, [copyText]);

  return (
    <ContextMenu
      asChild
      items={
        <MenuItem prefixIcon={<CopyIcon />} onSelect={handleCopySelect}>
          {t['Copy']()}
        </MenuItem>
      }
    >
      <Table
        {...tableProps}
        ref={tableRef}
        className={clsx(styles.erpGrid, className)}
        onContextMenuCapture={handleContextMenuCapture}
        onDoubleClick={handleDoubleClick}
        onMouseOut={handleMouseOut}
        onMouseOver={handleMouseOver}
        onPointerDownCapture={handlePointerDownCapture}
      />
    </ContextMenu>
  );
}
